import React, { useState, useEffect } from 'react';
import { Clock, MapPin, DollarSign, Info } from 'lucide-react';
import api from '../services/regiojet-api';
import { getStationName, getLocalizedStationName } from '../utils/stations';

const SeatSelection = ({ routeId, selectedRoute, onSeatSelect, passengers, t }) => {
  // States
  const [seats, setSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehicleType, setVehicleType] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Функция для форматирования даты/времени
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    
    // Если dateStr это объект Date, преобразуем его в строку
    if (dateStr instanceof Date) {
      return dateStr.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Если строка, преобразуем ее в формат времени
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // Если невалидная дата, возвращаем исходную строку
      
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Error formatting datetime:', err);
      return dateStr;
    }
  };

  // Fetch seats when route is selected
  useEffect(() => {
    const fetchSeats = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!routeId || !selectedRoute?.departureStationId || !selectedRoute?.arrivalStationId) {
          throw new Error('Missing required route data');
        }

        console.log('Selected route data:', {
          routeId,
          departureStationId: selectedRoute.departureStationId,
          arrivalStationId: selectedRoute.arrivalStationId,
          stations: selectedRoute.stations
        });

        const seatsData = await api.getFreeSeats(routeId, {
          fromStationId: selectedRoute.departureStationId,
          toStationId: selectedRoute.arrivalStationId,
          tariff: selectedRoute.tariff || 'REGULAR',
          seatClass: selectedRoute.seatClass || 'REGULAR'
        });

        if (!seatsData?.length) {
          setSeats([]);
          return;
        }

        setSeats(seatsData);
        
        // Определяем тип транспорта
        if (seatsData[0]?.vehicles?.[0]?.standardKey) {
          setVehicleType(
            seatsData[0].vehicles[0].standardKey.includes('TRAIN') ? 'train' : 'bus'
          );
        }

        // Устанавливаем первый вагон как выбранный по умолчанию
        if (seatsData[0]?.vehicles?.[0]) {
          setSelectedVehicle(seatsData[0].vehicles[0].number);
        }
      } catch (err) {
        console.error('Error fetching seats:', err);
        setError(err.message || t.errorFetchingSeats);
      } finally {
        setLoading(false);
      }
    };

    if (routeId && selectedRoute) {
      fetchSeats();
    }
  }, [routeId, selectedRoute, t]);

  // Handle seat selection
  const handleSeatSelect = (seat, vehicle) => {
    if (!seat.available) {
      return;
    }

    const seatData = {
      number: seat.number,
      vehicleNumber: vehicle.number,
      seatClass: seat.seatClass || 'REGULAR'
    };

    setSelectedSeats(prev => {
      // Если место уже выбрано - удаляем его
      if (prev.find(s => s.number === seat.number && s.vehicleNumber === vehicle.number)) {
        return prev.filter(s => !(s.number === seat.number && s.vehicleNumber === vehicle.number));
      }

      // Проверяем, не превышает ли количество выбранных мест количество пассажиров
      if (prev.length >= Number(passengers)) {
        return prev;
      }

      // Добавляем новое место
      return [...prev, seatData];
    });
  };

  // Функция для обработки выбора вагона/автобуса
  const handleVehicleSelect = (vehicleNumber) => {
    setSelectedVehicle(vehicleNumber);
  };

  // Функция для рендеринга мест в выбранном вагоне/автобусе
  const renderVehicleSeats = (vehicles) => {
    if (!selectedVehicle) return null;

    const vehicle = vehicles.find(v => v.number === selectedVehicle);
    if (!vehicle) return null;

    return (
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">
          {vehicleType === 'train' ? t.carNumber : t.busNumber} {selectedVehicle}
        </h3>
        
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2">
          {vehicle.seats
            .filter(seat => seat.number)
            .map(seat => {
              const isSelected = selectedSeats.find(
                s => s.number === seat.number && s.vehicleNumber === vehicle.number
              );
              
              const seatClasses = `
                w-10 h-10 rounded-md flex items-center justify-center
                ${!seat.available ? 'bg-gray-300 cursor-not-allowed' : 
                  isSelected ? 'bg-green-500 text-white' : 'bg-white border border-gray-300 hover:bg-gray-100'}
                ${selectedSeats.length >= Number(passengers) && !isSelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                transition-colors
              `;

              return (
                <div
                  key={`seat-${seat.number}`}
                  className={seatClasses}
                  onClick={() => seat.available && handleSeatSelect(seat, vehicle)}
                >
                  {!seat.available ? (
                    <span className="text-white">X</span>
                  ) : (
                    <span>{seat.number}</span>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  // Функция для рендеринга сидений в поезде
  const renderTrainSeats = (vehicle) => {
    const compartments = [];
    const seats = vehicle.seats.filter(seat => seat.number && seat.available !== false);
    
    // Группируем места по купе (4 места в каждом)
    for (let i = 0; i < seats.length; i += 4) {
      const compartmentSeats = seats.slice(i, i + 4);
      compartments.push(
        <div key={`compartment-${i}`} className="flex flex-col bg-gray-50 p-2 rounded-lg">
          <div className="grid grid-cols-2 gap-2">
            {compartmentSeats.map(seat => renderSeat(seat, vehicle))}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {compartments}
      </div>
    );
  };

  // Функция для рендеринга сидений в автобусе
  const renderBusSeats = (vehicle) => {
    const seats = vehicle.seats.filter(seat => seat.number);
    const rows = [];
    
    // Группируем места по рядам (4 места в ряду: 2+2)
    for (let i = 0; i < seats.length; i += 4) {
      const rowSeats = seats.slice(i, i + 4);
      rows.push(
        <div key={`row-${i}`} className="flex justify-center space-x-8">
          <div className="flex space-x-2">
            {rowSeats.slice(0, 2).map(seat => renderSeat(seat, vehicle))}
          </div>
          {/* Проход */}
          <div className="w-4 bg-gray-200 rounded"></div>
          <div className="flex space-x-2">
            {rowSeats.slice(2, 4).map(seat => renderSeat(seat, vehicle))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {/* Кабина водителя */}
        <div className="w-full h-12 bg-gray-300 rounded-t-xl mb-4"></div>
        {rows}
      </div>
    );
  };

  // Функция рендеринга отдельного места
  const renderSeat = (seat, vehicle) => {
    const isSelected = selectedSeats.find(
      s => s.number === seat.number && s.vehicleNumber === vehicle.number
    );
    
    const seatClasses = `
      seat 
      ${!seat.available ? 'bg-gray-300 cursor-not-allowed' : 
        isSelected ? 'bg-green-500 text-white' : 'bg-white hover:bg-gray-100'}
      ${selectedSeats.length >= Number(passengers) && !isSelected ? 'cursor-not-allowed opacity-50' : ''}
      p-2 rounded-md text-center cursor-pointer transition-colors
    `;

    return (
      <div
        key={seat.number}
        className={seatClasses}
        onClick={() => handleSeatSelect(seat, vehicle)}
      >
        <span className="text-sm">{seat.number}</span>
      </div>
    );
  };

  // Функция для определения класса вагона
  const getVehicleClass = (standardKey) => {
    const classMap = {
      'YELLOW': t.economyClass,
      'BLUE': t.businessClass,
      'RED': t.firstClass
    };

    const className = standardKey?.split('_').pop();
    return classMap[className] || className;
  };

  // Получение названий станций с учетом данных из route
  const getDepartureStationName = () => {
    // Проверяем наличие имени станции в самом маршруте
    if (selectedRoute.departureStationName) {
      return selectedRoute.departureStationName;
    }
    
    // Используем getLocalizedStationName как более надежный вариант
    return getLocalizedStationName(selectedRoute?.departureStationId, selectedRoute, t?.locale);
  };

  const getArrivalStationName = () => {
    // Проверяем наличие имени станции в самом маршруте
    if (selectedRoute.arrivalStationName) {
      return selectedRoute.arrivalStationName;
    }
    
    // Используем getLocalizedStationName как более надежный вариант
    return getLocalizedStationName(selectedRoute?.arrivalStationId, selectedRoute, t?.locale);
  };
  
  // Обрабатываем времена отправления и прибытия
  const departureTime = formatDateTime(selectedRoute?.departureTime);
  const arrivalTime = formatDateTime(selectedRoute?.arrivalTime);

  // Названия станций
  const departureStationName = getDepartureStationName();
  const arrivalStationName = getArrivalStationName();

  // Отладочный вывод
  console.log('Station names in SeatSelection:', {
    departureStationId: selectedRoute?.departureStationId,
    departureStationName,
    arrivalStationId: selectedRoute?.arrivalStationId,
    arrivalStationName,
    stations: selectedRoute?.stations
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">{t.loadingSeats}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center space-x-2 text-red-600 mb-2">
          <Info size={20} />
          <p className="font-medium">{error}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          {t.retry}
        </button>
      </div>
    );
  }

  // No seats available
  if (!seats.length) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-2">{t.noSeatsAvailable}</p>
        <p className="text-sm text-gray-500">{t.selectDifferentRoute}</p>
      </div>
    );
  }

  // Main content
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">{t.selectSeats || 'Выберите места'}</h2>
        <div className="text-gray-500 text-sm">
          {t.selectedSeats || 'Выбранные места'}: {selectedSeats.length}/{passengers}
        </div>
      </div>

      {/* Информация о маршруте */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 text-sm">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-gray-400" />
          <span>{departureTime} - {arrivalTime}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-gray-400" />
          <span>
            {departureStationName} - {arrivalStationName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-gray-400" />
          <span>{getVehicleClass(seats[0]?.vehicles?.[0]?.standardKey)}</span>
        </div>
      </div>

      {/* Легенда */}
      <div className="flex gap-4 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
          <span className="text-sm">{t.available || 'Свободно'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-sm">{t.selected || 'Выбрано'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span className="text-sm">{t.occupied || 'Занято'}</span>
        </div>
      </div>

      {/* Выбор вагона/автобуса */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">{t.selectVehicle || 'Выберите вагон'}</h3>
        <div className="flex flex-wrap gap-2">
          {seats[0]?.vehicles?.map(vehicle => (
            <div 
              key={`vehicle-btn-${vehicle.number}`}
              onClick={() => handleVehicleSelect(vehicle.number)}
              className={`
                py-2 px-4 rounded-md cursor-pointer transition-colors
                ${selectedVehicle === vehicle.number 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'}
              `}
            >
              {vehicle.number}
            </div>
          ))}
        </div>
      </div>

      {/* Отображение схемы мест выбранного вагона */}
      {seats.map((section, sIndex) => (
        <div key={`section-${sIndex}`} className="mb-8">
          {renderVehicleSeats(section.vehicles)}
        </div>
      ))}

      {/* Выбранные места */}
      {selectedSeats.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="font-medium mb-2">{t.selectedSeats || 'Выбранные места'}</h3>
          <div className="flex flex-wrap gap-2">
            {selectedSeats.map((seat, idx) => (
              <div 
                key={`selected-seat-${idx}`}
                className="bg-yellow-200 text-yellow-800 py-1 px-3 rounded-full"
              >
                {t.vehicle || 'Вагон'} {seat.vehicleNumber}, {t.seat || 'Место'} {seat.number}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Кнопка продолжения */}
      <div className="flex justify-end">
        <button
          className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          onClick={() => onSeatSelect(selectedSeats)}
          disabled={selectedSeats.length !== parseInt(passengers)}
        >
          {t.continue || 'Продолжить'}
        </button>
      </div>
    </div>
  );
};

export default SeatSelection;